import React, { useState } from 'react';
import { X, Bell, Send } from 'lucide-react';
import { useRbac } from '../context';
import { createNotification, NotificationType, CreateNotificationRequest } from '../services/notificationApi';
import { toast } from 'react-hot-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

interface CreateNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateNotificationModal: React.FC<CreateNotificationModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { user } = useRbac();

  const availableRoles = [
    { value: 'SHM', label: 'SHM' },
    { value: 'HM', label: 'HM' },
    { value: 'HR', label: 'HR' },
    { value: 'FBDE', label: 'FBDE' },
    { value: 'CONSULTANT', label: 'CONSULTANT' },
    { value: 'MODERATOR', label: 'MODERATOR' },
    { value: 'BDE', label: 'BDE' },
    { value: 'TPO', label: 'TPO' },
    { value: 'FREELANCE_COUNSELLOR', label: 'Freelance Counsellor' },
    { value: 'INTERNAL_TEAM', label: 'Internal Team' }
  ];

  const allRoleValues = availableRoles.map((role) => role.value);

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState<NotificationType>(NotificationType.REGULAR);
  // Initially select all target roles for ADMIN; none for other roles
  const [targetRoles, setTargetRoles] = useState<string[]>(user?.role === 'ADMIN' ? allRoleValues : []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!user) {
    return null;
  }

  // HR users cannot create notifications
  if (user.role === 'HR') {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !message.trim()) {
      toast.error('Please fill in both title and message');
      return;
    }

    if (title.length > 200) {
      toast.error('Title cannot exceed 200 characters');
      return;
    }

    if (message.length > 3000) {
      toast.error('Message cannot exceed 3000 characters');
      return;
    }

    try {
      setIsSubmitting(true);
      const notificationData: CreateNotificationRequest = {
        title: title.trim(),
        message: message.trim(),
        type: type || NotificationType.REGULAR,
        targetRoles: user.role === 'ADMIN' && targetRoles.length > 0 ? targetRoles : undefined
      };

      await createNotification(notificationData);
      toast.success('Notification created successfully!');
      
      // Reset form
      setTitle('');
      setMessage('');
      setType(NotificationType.REGULAR);
      setTargetRoles(user?.role === 'ADMIN' ? allRoleValues : []);
      
      // Close modal and refresh notifications
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error creating notification:', error);
      toast.error(error.response?.data?.message || 'Failed to create notification');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setTitle('');
      setMessage('');
      setType(NotificationType.REGULAR);
      setTargetRoles(user?.role === 'ADMIN' ? allRoleValues : []);
      onClose();
    }
  };
  
  const toggleRole = (role: string) => {
    setTargetRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-orange-600" />
            <DialogTitle className="text-lg font-semibold">
              Create Notification
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 px-6">
          <form onSubmit={handleSubmit} className="space-y-4" id="notification-form">
          {/* Notification Type - Only ADMIN can create ADMIN_POPUP */}
          {user.role === 'ADMIN' && (
            <div className="space-y-2">
              <Label htmlFor="type">Notification Type</Label>
              <Select
                value={type}
                onValueChange={(value) => setType(value as NotificationType)}
              >
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NotificationType.REGULAR}>
                    Regular Notification
                  </SelectItem>
                  <SelectItem value={NotificationType.ADMIN_POPUP}>
                    Admin Popup (Shows in popup)
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {type === NotificationType.ADMIN_POPUP
                  ? 'This notification will appear as a popup for all target users'
                  : 'This notification will appear in the notification bell'}
              </p>
            </div>
          )}

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter notification title"
              maxLength={200}
              required
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500">
              {title.length}/200 characters
            </p>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">
              Message <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter notification message"
              rows={6}
              maxLength={3000}
              required
              disabled={isSubmitting}
              className="resize-none"
            />
            <p className="text-xs text-gray-500">
              {message.length}/3000 characters
            </p>
          </div>

          {/* Target User Types - Only for ADMIN */}
          {user.role === 'ADMIN' && (
            <div className="space-y-2">
              <Label>Target User Types (Optional)</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3 border rounded-lg">
                {availableRoles.map((role) => (
                  <div key={role.value} className="flex items-center space-x-2 min-w-0 flex-shrink-0">
                    <input
                      type="checkbox"
                      id={`role-${role.value}`}
                      checked={targetRoles.includes(role.value)}
                      onChange={() => toggleRole(role.value)}
                      className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500 focus:ring-2 accent-orange-600 flex-shrink-0"
                      style={{ accentColor: '#ea580c', minWidth: '20px', minHeight: '20px' }}
                    />
                    <label
                      htmlFor={`role-${role.value}`}
                      className="text-sm text-gray-700 cursor-pointer truncate flex-1 min-w-0"
                      title={role.label}
                    >
                      {role.label}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                {targetRoles.length === 0
                  ? 'If no roles selected, notification will be sent to all users in your hierarchy'
                  : `Notification will be sent to users with roles: ${targetRoles.join(', ')}`}
              </p>
            </div>
          )}

          {/* Info */}
          {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This notification will be sent to all users in your hierarchy (including HR users).
            </p>
          </div> */}

          </form>
        </div>

        {/* Actions - Fixed at bottom */}
        <div className="flex-shrink-0 flex items-center justify-end gap-2 px-6 pt-4 pb-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="notification-form"
            disabled={isSubmitting || !title.trim() || !message.trim()}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {isSubmitting ? (
              <>Sending...</>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Create Notification
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateNotificationModal;

